<script lang="ts">
	import { _ } from "svelte-i18n";
	import { onDestroy } from "svelte";
	import { lastValueFrom, tap } from "rxjs";
	import CircularProgressBar from "../../../common/createPresentation/CircularProgressBar.svelte";
	import { countDown } from "../../../common/createPresentation/logic/countDown";

	let {
		mediaKind,
		value = $bindable(""),
	}: {
		mediaKind: "image" | "audio";
		value?: string;
	} = $props();

	type Mode = "upload" | "record";
	let mode = $state<Mode>("upload");

	let fileInput: HTMLInputElement;

	let videoEl = $state<HTMLVideoElement | undefined>(undefined);
	let canvasEl = $state<HTMLCanvasElement | undefined>(undefined);
	let cameraStream = $state<MediaStream | null>(null);
	let captureInProgress = $state(false);
	let countDownProgress = $state<
		{ fractionComplete: number; timeLeftMiliseconds: number } | undefined
	>(undefined);

	let micStream = $state<MediaStream | null>(null);
	let mediaRecorder = $state<MediaRecorder | null>(null);
	let audioChunks: BlobPart[] = [];
	let recordingAudio = $state(false);

	let errorMessage = $state("");

	$effect(() => {
		if (videoEl) {
			videoEl.srcObject = cameraStream;
		}
	});

	onDestroy(stopAllStreams);

	function stopAllStreams() {
		cameraStream?.getTracks().forEach((t) => t.stop());
		cameraStream = null;
		micStream?.getTracks().forEach((t) => t.stop());
		micStream = null;
		if (mediaRecorder && mediaRecorder.state !== "inactive") {
			try {
				mediaRecorder.stop();
			} catch {
				/* ignored */
			}
		}
		mediaRecorder = null;
	}

	function clearError() {
		errorMessage = "";
	}

	async function setMode(newMode: Mode) {
		clearError();
		if (newMode === mode) return;
		stopAllStreams();
		mode = newMode;
		if (newMode === "record") {
			try {
				if (mediaKind === "image") {
					cameraStream = await navigator.mediaDevices.getUserMedia({ video: true });
				} else {
					micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
				}
			} catch (err) {
				console.warn("UploadOrRecord: media permission denied or unsupported", err);
				const isPermissionDenied = err instanceof DOMException && err.name === "NotAllowedError";
				errorMessage = isPermissionDenied
					? $_("landingpage.newPresentation.customPersona.uploadOrRecordPermissionDenied")
					: $_("landingpage.newPresentation.customPersona.uploadOrRecordUnsupported");
				mode = "upload";
			}
		}
	}

	async function fileToDataUrl(file: File): Promise<string> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onerror = () => reject(reader.error ?? new Error("read failed"));
			reader.onload = () => resolve(String(reader.result ?? ""));
			reader.readAsDataURL(file);
		});
	}

	async function handleFileInput(event: Event) {
		clearError();
		const input = event.target as HTMLInputElement;
		const file = input.files?.[0];
		if (!file) return;
		try {
			value = await fileToDataUrl(file);
		} catch (err) {
			console.warn("UploadOrRecord: file read failed", err);
			errorMessage = $_("landingpage.newPresentation.customPersona.uploadError");
		}
	}

	async function handleDrop(event: DragEvent) {
		event.preventDefault();
		clearError();
		const file = event.dataTransfer?.files[0];
		if (!file) return;
		const expectedPrefix = `${mediaKind}/`;
		if (!file.type.startsWith(expectedPrefix)) return;
		try {
			value = await fileToDataUrl(file);
		} catch (err) {
			console.warn("UploadOrRecord: drop read failed", err);
			errorMessage = $_("landingpage.newPresentation.customPersona.uploadError");
		}
	}

	function preventDefault(event: Event) {
		event.preventDefault();
	}

	async function captureImage() {
		if (!videoEl || !canvasEl || !cameraStream) return;
		captureInProgress = true;
		clearError();
		try {
			await lastValueFrom(
				countDown(3).pipe(
					tap((progress) => {
						countDownProgress = progress;
					}),
				),
			);
			const v = videoEl;
			const c = canvasEl;
			c.width = v.videoWidth || 640;
			c.height = v.videoHeight || 480;
			const ctx = c.getContext("2d");
			if (!ctx) throw new Error("no 2d context");
			ctx.drawImage(v, 0, 0, c.width, c.height);
			value = c.toDataURL("image/png");
			stopCameraStream();
		} finally {
			countDownProgress = undefined;
			captureInProgress = false;
		}
	}

	function stopCameraStream() {
		cameraStream?.getTracks().forEach((t) => t.stop());
		cameraStream = null;
		if (videoEl) {
			videoEl.srcObject = null;
		}
	}

	async function retakeImage() {
		clearError();
		value = "";
		try {
			cameraStream = await navigator.mediaDevices.getUserMedia({ video: true });
		} catch (err) {
			console.warn("UploadOrRecord: retake camera open failed", err);
			const isPermissionDenied = err instanceof DOMException && err.name === "NotAllowedError";
			errorMessage = isPermissionDenied
				? $_("landingpage.newPresentation.customPersona.uploadOrRecordPermissionDenied")
				: $_("landingpage.newPresentation.customPersona.uploadOrRecordUnsupported");
			mode = "upload";
		}
	}

	function startAudioRecording() {
		if (!micStream) return;
		clearError();
		audioChunks = [];
		let recorder: MediaRecorder;
		try {
			recorder = new MediaRecorder(micStream);
		} catch (err) {
			console.warn("UploadOrRecord: MediaRecorder construction failed", err);
			errorMessage = $_("landingpage.newPresentation.customPersona.uploadOrRecordUnsupported");
			return;
		}
		const mimeType = recorder.mimeType || "audio/webm";
		recorder.ondataavailable = (e) => {
			if (e.data.size > 0) audioChunks.push(e.data);
		};
		recorder.onstop = async () => {
			const blob = new Blob(audioChunks, { type: mimeType });
			value = await blobToDataUrl(blob);
		};
		recorder.start();
		mediaRecorder = recorder;
		recordingAudio = true;
	}

	function stopAudioRecording() {
		try {
			mediaRecorder?.stop();
		} catch (err) {
			console.warn("UploadOrRecord: stop failed", err);
		}
		recordingAudio = false;
	}

	function blobToDataUrl(blob: Blob): Promise<string> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onerror = () => reject(reader.error ?? new Error("read failed"));
			reader.onload = () => resolve(String(reader.result ?? ""));
			reader.readAsDataURL(blob);
		});
	}
</script>

<div class="upload-or-record">
	<div class="mode-tabs" role="tablist">
		<button
			type="button"
			role="tab"
			aria-selected={mode === "upload"}
			class:active={mode === "upload"}
			onclick={() => setMode("upload")}
		>
			{$_("landingpage.newPresentation.customPersona.uploadOrRecordUploadTab")}
		</button>
		<button
			type="button"
			role="tab"
			aria-selected={mode === "record"}
			class:active={mode === "record"}
			onclick={() => setMode("record")}
		>
			{$_("landingpage.newPresentation.customPersona.uploadOrRecordRecordTab")}
		</button>
	</div>

	{#if mode === "upload"}
		<div
			class="dropzone"
			onclick={() => fileInput.click()}
			onkeydown={(e) => e.key === "Enter" && fileInput.click()}
			ondrop={handleDrop}
			ondragover={preventDefault}
			role="button"
			tabindex="0"
		>
			{#if mediaKind === "image"}
				{$_("landingpage.newPresentation.customPersona.uploadOrRecordDropImage")}
			{:else}
				{$_("landingpage.newPresentation.customPersona.uploadOrRecordDropAudio")}
			{/if}
			<input
				bind:this={fileInput}
				type="file"
				accept={mediaKind === "image" ? "image/*" : "audio/*"}
				onchange={handleFileInput}
				hidden
			/>
		</div>
	{:else if mediaKind === "image"}
		<div class="record-area">
			<canvas bind:this={canvasEl} hidden></canvas>
			{#if value}
				<!--
					Captured frame replaces the live feed. The camera stream is
					stopped in `captureImage`; retake re-opens it.
				-->
				<img class="record-preview" src={value} alt="" />
				<button type="button" class="record-button" onclick={retakeImage}>
					{$_("landingpage.newPresentation.customPersona.uploadOrRecordRetake")}
				</button>
			{:else}
				<div class="video-wrapper">
					<!-- svelte-ignore a11y-media-has-caption -->
					<video bind:this={videoEl} autoplay muted playsinline class="record-video"></video>
					{#if countDownProgress}
						<div class="progress-overlay">
							<CircularProgressBar
								fraction={1 - countDownProgress.fractionComplete}
								color="var(--bs-primary)"
								backgroundOpacity={0.7}
							>
								<span class="countdown-timer">
									{Math.ceil(countDownProgress.timeLeftMiliseconds / 1000)}
								</span>
							</CircularProgressBar>
						</div>
					{/if}
				</div>
				<button
					type="button"
					class="record-button"
					onclick={captureImage}
					disabled={captureInProgress || !cameraStream}
				>
					{$_("landingpage.newPresentation.customPersona.uploadOrRecordCapture")}
				</button>
			{/if}
		</div>
	{:else}
		<div class="record-area">
			<button
				type="button"
				class="record-button"
				onclick={recordingAudio ? stopAudioRecording : startAudioRecording}
				disabled={!micStream}
			>
				{#if recordingAudio}
					{$_("landingpage.newPresentation.customPersona.uploadOrRecordStopRecording")}
				{:else}
					{$_("landingpage.newPresentation.customPersona.uploadOrRecordStartRecording")}
				{/if}
			</button>
		</div>
	{/if}

	{#if errorMessage}
		<div class="error">{errorMessage}</div>
	{/if}

	<!--
		Bottom preview. The image-record branch already renders the captured
		image inline (replacing the live feed), so we skip it here to avoid
		showing the picture twice.
	-->
	{#if value && !(mode === "record" && mediaKind === "image")}
		<div class="preview">
			{#if mediaKind === "image"}
				<img src={value} alt="" />
			{:else}
				<!-- svelte-ignore a11y-media-has-caption -->
				<audio src={value} controls></audio>
			{/if}
		</div>
	{/if}
</div>

<style>
	.upload-or-record {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
	}
	.mode-tabs {
		display: inline-flex;
		gap: 0;
		border: 1px solid var(--bs-border-color, #dee2e6);
		border-radius: 0.375rem;
		overflow: hidden;
		align-self: flex-start;
	}
	.mode-tabs button {
		background: transparent;
		border: none;
		padding: 0.25rem 0.75rem;
		cursor: pointer;
		font-size: 0.875rem;
	}
	.mode-tabs button.active {
		background: var(--bs-primary, #0d6efd);
		color: #fff;
	}
	.dropzone {
		padding: 1rem;
		border: 2px dashed var(--bs-border-color, #ccc);
		border-radius: 0.375rem;
		text-align: center;
		cursor: pointer;
		background: var(--bs-body-bg, #fff);
	}
	.dropzone:hover {
		border-color: var(--bs-primary, #0d6efd);
	}
	.record-area {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
		align-items: flex-start;
	}
	.video-wrapper {
		position: relative;
		width: 100%;
		max-width: 360px;
	}
	.record-video {
		width: 100%;
		max-width: 360px;
		display: block;
		background: #000;
		border-radius: 0.375rem;
	}
	.record-preview {
		width: 100%;
		max-width: 360px;
		max-height: 360px;
		object-fit: contain;
		background: #000;
		border-radius: 0.375rem;
	}
	.progress-overlay {
		position: absolute;
		bottom: 0.75rem;
		left: 50%;
		transform: translateX(-50%);
		height: 3rem;
		aspect-ratio: 1;
		display: flex;
		justify-content: center;
		align-items: center;
		border: 0.25rem solid white;
		border-radius: 50%;
		box-sizing: border-box;
	}
	.countdown-timer {
		color: white;
		font-variant-numeric: tabular-nums;
		font-size: 1.25rem;
		font-weight: bold;
		line-height: 1;
	}
	.record-button {
		padding: 0.5rem 1rem;
		border-radius: 0.375rem;
		border: 1px solid var(--bs-primary, #0d6efd);
		background: var(--bs-primary, #0d6efd);
		color: #fff;
		cursor: pointer;
	}
	.record-button:disabled {
		opacity: 0.6;
		cursor: default;
	}
	.error {
		color: var(--bs-danger, #dc3545);
		font-size: 0.875rem;
	}
	.preview img {
		max-width: 100%;
		max-height: 200px;
		border-radius: 0.375rem;
		object-fit: contain;
	}
	.preview audio {
		width: 100%;
	}
</style>
